(function () {
  const PICKER = 'variant-picker';
  const STATE = new WeakMap();
  const PRODUCT_CACHE = new Map();

  function getState(picker) {
    let state = STATE.get(picker);
    if (!state) {
      state = { variantId: null, priceText: null, observer: null, token: 0 };
      STATE.set(picker, state);
    }
    return state;
  }

  function currentProductUrl(picker) {
    const raw = picker.dataset.productUrl || window.location.pathname;
    const url = new URL(raw, window.location.origin);
    url.search = '';
    url.hash = '';
    return url;
  }

  async function getProduct(picker) {
    const url = currentProductUrl(picker);
    const key = url.pathname;

    if (!PRODUCT_CACHE.has(key)) {
      const js = new URL(url.toString());
      js.pathname = js.pathname.replace(/\/$/, '') + '.js';

      PRODUCT_CACHE.set(
        key,
        fetch(js.toString(), {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        }).then(function (response) {
          if (!response.ok) throw new Error('Product JSON failed: ' + response.status);
          return response.json();
        })
      );
    }

    return PRODUCT_CACHE.get(key);
  }

  function selectedElementFromEvent(target) {
    if (target instanceof HTMLSelectElement) {
      return target.options[target.selectedIndex] || null;
    }
    if (target instanceof HTMLInputElement) return target;
    return null;
  }

  function selectedElementFromPicker(picker) {
    const selected = picker.querySelector('fieldset input:checked, select option:checked');
    return selected instanceof HTMLElement ? selected : null;
  }

  function variantIdFromElement(element) {
    const value = element && element.dataset ? element.dataset.variantId : null;
    return value ? String(value) : null;
  }

  function productPriceElement(picker) {
    const scope = picker.closest('.shopify-section, main, dialog') || document;
    const productId = picker.dataset.productId;

    if (productId) {
      const exact = scope.querySelector('product-price[data-product-id="' + CSS.escape(productId) + '"]');
      if (exact) return exact;
    }
    return scope.querySelector('product-price');
  }

  function priceContainer(picker) {
    return productPriceElement(picker)?.querySelector('[ref="priceContainer"]') || null;
  }

  function formatMoney(cents) {
    const amount = Number(cents) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function writePrice(picker) {
    const state = getState(picker);
    if (!state.priceText) return;

    const container = priceContainer(picker);
    if (!container) return;

    const regularPrice = container.querySelector('.price');
    if (regularPrice) {
      const visibleText = Array.from(regularPrice.childNodes)
        .filter(function (node) { return node.nodeType === Node.TEXT_NODE; })
        .map(function (node) { return node.textContent || ''; })
        .join('')
        .trim();

      if (visibleText !== state.priceText) {
        Array.from(regularPrice.childNodes).forEach(function (node) {
          if (node.nodeType === Node.TEXT_NODE) node.remove();
        });
        regularPrice.appendChild(document.createTextNode(state.priceText));
      }
    } else {
      container.innerHTML = '<span class="price">' + state.priceText + '</span>';
    }
  }

  function enforcePrice(picker) {
    const state = getState(picker);
    const priceEl = productPriceElement(picker);
    if (!priceEl) return;

    if (state.observer) state.observer.disconnect();

    state.observer = new MutationObserver(function () {
      writePrice(picker);
    });

    state.observer.observe(priceEl, {
      childList: true,
      subtree: true,
      characterData: true
    });

    writePrice(picker);
  }

  function updateVariantInputs(picker, variantId) {
    const root = picker.closest('product-form-component, .product-details, .shopify-section') || document;
    root.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
      input.value = variantId;
      input.setAttribute('value', variantId);
    });
  }

  function updateUrl(variantId) {
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === variantId) return;
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());
  }

  async function setExactVariant(picker, selectedElement) {
    const state = getState(picker);
    state.token += 1;
    const token = state.token;

    const directVariantId = variantIdFromElement(selectedElement);
    if (!directVariantId) return;

    try {
      const product = await getProduct(picker);
      if (token !== state.token) return;

      const variant = Array.isArray(product.variants)
        ? product.variants.find(function (item) { return String(item.id) === directVariantId; })
        : null;

      if (!variant) return;

      state.variantId = directVariantId;
      state.priceText = formatMoney(variant.price);

      updateVariantInputs(picker, directVariantId);
      updateUrl(directVariantId);
      enforcePrice(picker);

      // Apps and Horizon may asynchronously repaint after the click. Keep correcting
      // the visible price to the exact clicked variant for a few seconds.
      [50, 150, 300, 600, 1000, 1600, 2500, 4000].forEach(function (delay) {
        setTimeout(function () {
          if (token === state.token) writePrice(picker);
        }, delay);
      });
    } catch (error) {
      console.warn('[Toujours] Direct variant price sync failed', error);
    }
  }

  document.addEventListener(
    'change',
    function (event) {
      if (!(event.target instanceof Element)) return;

      const picker = event.target.closest(PICKER);
      if (!picker) return;

      const selected = selectedElementFromEvent(event.target);
      if (!selected) return;

      // Run after the browser commits the new checked/selected state, but do not
      // wait for Globo/Horizon asynchronous requests.
      queueMicrotask(function () {
        setExactVariant(picker, selected);
      });
    },
    true
  );

  function normalizeAll() {
    document.querySelectorAll(PICKER).forEach(function (picker) {
      if (!(picker instanceof HTMLElement)) return;
      const selected = selectedElementFromPicker(picker);
      if (selected) setExactVariant(picker, selected);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeAll, { once: true });
  } else {
    normalizeAll();
  }

  window.addEventListener('load', normalizeAll, { once: true });
})();